import {
  findPlaceFromQuery,
  geocodeLatLng,
  geolocationToLatLng,
  getPlaceDetails,
  getPlacePredictions,
  getUkLatLngBounds,
  loadGoogle
} from "@justpark/helpers/src/maps/maps";
import debounce from "debounce-promise-with-cancel";
import getDriveUpListings from "@justpark/api/src/requests/getDriveUpListings";
import { getGeolocation } from "@justpark/helpers/src/geolocation/geolocation";

const DEBOUNCE_TIME = 250;

export default class LocationSearchAPI {
  sessionToken = null;

  apiClient = null;

  constructor(apiClient) {
    this.apiClient = apiClient;

    this.debouncedGoogleLookUp = debounce(
      (text) => this.googleLookUp(text),
      DEBOUNCE_TIME
    );

    this.debouncedDriveupLookUp = debounce(
      (text) => this.driveupLookUp(text),
      DEBOUNCE_TIME
    );
  }

  // eslint-disable-next-line class-methods-use-this
  getCurrentLocation() {
    return getGeolocation({ maximumAge: 120000 })
      .then(geolocationToLatLng)
      .then(geocodeLatLng)
      .then((geocoderResult) => {
        const lat = geocoderResult[0].geometry.location.lat();
        const lng = geocoderResult[0].geometry.location.lng();
        const address = geocoderResult[0].formatted_address;

        return { lat, lng, address };
      });
  }

  driveupLookUp(input) {
    const queryString =
      input.substring(0, 1) === "#" ? input.substring(1) : input;

    const isThreeNumbers = /^\d{3,}/;

    if (!queryString.match(isThreeNumbers)) {
      return Promise.resolve([]);
    }

    return this.apiClient(getDriveUpListings(queryString))
      .then((response) => {
        return response.data.data.slice(0, 3).map((item) => ({
          type: "justpark",
          place_id: item.id,
          html: `<b>${item.id}:</b> ${item.title}`,
          description: `${item.id}: ${item.title}`
        }));
      })
      .catch((err) => {
        console.error(err);
        return [];
      });
  }

  getGoogleSessionToken() {
    return loadGoogle().then((g) => {
      if (this.sessionToken === null) {
        this.sessionToken = new g.maps.places.AutocompleteSessionToken();
      }

      return this.sessionToken;
    });
  }

  static getHtmlFromGoogleResult(item) {
    const { description: str } = item;

    if (item.matched_substrings && item.matched_substrings.length > 0) {
      const { length, offset } = item.matched_substrings[0];

      return `${str.substr(0, offset)}<b>${str.substr(
        offset,
        length
      )}</b>${str.substr(offset + length)}`;
    }

    return str;
  }

  googleLookUp(input) {
    return this.getGoogleSessionToken()
      .then((sessionToken) =>
        getPlacePredictions({
          input,
          bounds: getUkLatLngBounds(),
          sessionToken
        })
      )
      .catch((err) => {
        // If no prediction results fallback to a query search
        if (err.message === "ZERO_RESULTS") {
          return findPlaceFromQuery(input).then((queryResults) => {
            return queryResults.map((result) => ({
              ...result,
              description: result.formatted_address
            }));
          });
        }

        throw err;
      })
      .then((results) =>
        results.map((result) => {
          return {
            ...result,
            type: "google",
            html: LocationSearchAPI.getHtmlFromGoogleResult(result)
          };
        })
      )
      .catch((err) => {
        throw err;
      });
  }

  googleDetails(googlePlaceId) {
    return getPlaceDetails(googlePlaceId, this.sessionToken).then((details) => {
      const coords = {
        lat: details.geometry.location.lat(),
        lng: details.geometry.location.lng()
      };

      this.sessionToken = null;

      return {
        googlePlaceId,
        coords
      };
    });
  }

  search(query) {
    this.debouncedDriveupLookUp.cancel();
    this.debouncedGoogleLookUp.cancel();

    const driveUpsPromise = this.debouncedDriveupLookUp(query);
    const googlePromise = this.debouncedGoogleLookUp(query);

    return Promise.allSettled([driveUpsPromise, googlePromise]).then(
      (results) => {
        // Get the fulfilled promises, concat their values
        const data = results
          .filter((item) => item.status === "fulfilled")
          .reduce((acc, curr) => {
            return curr.value.length > 0 ? acc.concat(curr.value) : acc;
          }, []);

        return data.length > 0
          ? data
          : [
              {
                type: "message",
                html: "No results found"
              }
            ];
      }
    );
  }
}
